import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [ident, setIdent] = useState("");        // email or username input
  const [email, setEmail] = useState("");        // canonical email (returned by verify)
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email: ident });
      toast.success("Jika terdaftar, kode dikirim ke email Anda"); setStep(2);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-reset-code", { email: ident, code });
      setEmail(data.email);   // canonical email for step 3
      toast.success("Kode benar. Buat password baru."); setStep(3);
    } catch (err) { toast.error(err?.response?.data?.detail || "Kode salah"); }
    finally { setBusy(false); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password minimal 8 karakter"); return; }
    if (pw !== confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Password diperbarui. Silakan login."); nav("/login");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bizma-cream flex items-center justify-center px-4 py-12" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6"><ArrowLeft size={16}/> Kembali ke Login</Link>
        <div className="felt-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo-bizma.png" alt="BIZMA" className="w-11 h-11 rounded-full object-cover shadow-sm" />
            <h1 className="text-2xl font-display font-semibold">Lupa Password</h1>
          </div>

          <div className="flex items-center gap-2 mb-6 text-xs">
            {[1,2,3].map(n => (
              <div key={n} className="flex items-center gap-2 flex-1">
                <div className={`w-7 h-7 rounded-full grid place-items-center font-semibold ${step>=n?"bg-bizma-terracotta text-white":"bg-bizma-muted text-bizma-subtext"}`}>{n}</div>
                {n<3 && <div className={`flex-1 h-0.5 ${step>n?"bg-bizma-terracotta":"bg-bizma-border"}`}/>}
              </div>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={sendCode} className="space-y-3" data-testid="forgot-step-1">
              <label className="text-sm font-medium">Username atau Email</label>
              <input required value={ident} onChange={(e)=>setIdent(e.target.value)} className="input-bizma" placeholder="username atau nama@email.com" data-testid="forgot-ident" />
              <button disabled={busy} className="btn-primary w-full h-12" data-testid="forgot-send-btn">{busy?"Mengirim...":"Kirim Kode"} <ArrowRight size={16}/></button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyCode} className="space-y-3" data-testid="forgot-step-2">
              <p className="text-sm text-bizma-subtext">Masukkan kode 6 digit yang dikirim ke email terdaftar.</p>
              <label className="text-sm font-medium">Kode Verifikasi</label>
              <input required value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))} className="input-bizma tracking-[0.4em] text-center text-xl" placeholder="000000" data-testid="forgot-code" />
              <button disabled={busy || code.length !== 6} className="btn-primary w-full h-12" data-testid="forgot-verify-btn">{busy?"Memverifikasi...":"Verifikasi Kode"} <ArrowRight size={16}/></button>
              <button type="button" onClick={()=>setStep(1)} className="w-full text-sm text-bizma-subtext" data-testid="forgot-back-1">Ubah email/username</button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitPw} className="space-y-3" data-testid="forgot-step-3">
              <p className="text-sm text-bizma-subtext">Kode valid. Buat password baru untuk akun <span className="font-semibold text-bizma-ink">{email}</span></p>
              <label className="text-sm font-medium">Password Baru</label>
              <PasswordInput required value={pw} onChange={(e)=>setPw(e.target.value)} placeholder="Min 8 karakter" autoComplete="new-password" testId="forgot-new-pw" />
              <label className="text-sm font-medium">Konfirmasi Password Baru</label>
              <PasswordInput required value={confirm} onChange={(e)=>setConfirm(e.target.value)} placeholder="Ulangi password" autoComplete="new-password" testId="forgot-confirm-pw" />
              <button disabled={busy} className="btn-primary w-full h-12" data-testid="forgot-reset-btn">{busy?"Memperbarui...":"Perbarui Password"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
